import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft, Check, ShieldCheck } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { FlowLines } from '@/components/generals/flow-lines'
import { ExpensesWidget, IncomeWidget } from '@/components/dashboard/finance-widgets'
import { LEGAL_NAV } from '@/constants/navigation'

const BENEFITS = [
    'Live dashboards from day one, with sample data to explore',
    'Read-only, encrypted connections to your accounts',
    'Guided onboarding with a finance specialist'
]

interface AuthLayoutProps {
    title: ReactNode
    description: ReactNode
    children: ReactNode
}

export const AuthLayout: FC<AuthLayoutProps> = ({ title, description, children }): ReactNode => {
    return (
        <div className='grid min-h-screen bg-cream lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
            <div className='flex flex-col px-5 py-6 sm:px-10 lg:px-16'>
                <header className='flex items-center justify-between gap-4'>
                    <Link href='/' aria-label='Veyra, back to home' className='rounded-md'>
                        <Logo />
                    </Link>

                    <Link href='/' className='group inline-flex items-center gap-1.5 text-sm text-stone-dark transition-colors hover:text-burgundy'>
                        <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-0.5' />
                        Back to site
                    </Link>
                </header>

                <main id='main' className='flex flex-1 items-center py-16'>
                    <div className='animate-rise mx-auto w-full max-w-md'>
                        <h1 className='font-heading text-display-md font-medium text-burgundy'>{title}</h1>
                        <div className='mt-4 leading-relaxed text-stone-dark'>{description}</div>
                        <div className='mt-10'>{children}</div>
                    </div>
                </main>

                <footer className='flex flex-wrap items-center justify-between gap-3 text-xs text-stone'>
                    <p suppressHydrationWarning>&copy; {new Date().getFullYear()} Veyra, Inc.</p>

                    <ul className='flex gap-5'>
                        {LEGAL_NAV.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='transition-colors hover:text-burgundy'>{link.label}</Link>
                            </li>
                        ))}
                    </ul>
                </footer>
            </div>

            <aside aria-label='Why Veyra' className='relative hidden overflow-hidden bg-burgundy text-cream lg:flex lg:flex-col lg:justify-between lg:p-14 xl:p-20'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                    <FlowLines tone='light' />
                </div>

                <div className='relative'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-beige'>Smarter financial management</p>

                    <p className='mt-6 max-w-md font-heading text-display-sm font-medium'>
                        Spend less time managing numbers and more time making confident decisions.
                    </p>

                    <ul className='mt-10 space-y-3.5'>
                        {BENEFITS.map(benefit => (
                            <li key={benefit} className='flex items-start gap-3 text-sm text-cream/80'>
                                <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-cream/10 text-gold'>
                                    <Check aria-hidden='true' className='size-3' />
                                </span>
                                {benefit}
                            </li>
                        ))}
                    </ul>
                </div>

                <div aria-hidden='true' className='relative mt-14 h-[360px]'>
                    <div className='absolute left-0 top-0 w-[300px] -rotate-2 animate-float-slow'>
                        <IncomeWidget animation='autoplay' delay={500} legendCount={3} />
                    </div>

                    <div className='absolute bottom-0 right-0 w-[300px] rotate-2 animate-float' style={{ animationDelay: '-2s' }}>
                        <ExpensesWidget animation='autoplay' delay={700} legendCount={3} />
                    </div>
                </div>

                <div className='relative mt-14 flex items-center gap-4 border-t border-cream/10 pt-6'>
                    <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-gold'>
                        <ShieldCheck aria-hidden='true' className='size-5' />
                    </span>

                    <p className='text-sm leading-relaxed text-cream/70'>
                        <span className='block font-medium text-cream'>Trusted by 500+ finance teams</span>
                        Bank-grade encryption and read-only access on every connection.
                    </p>
                </div>
            </aside>
        </div>
    )
}
